import {
  Duration,
  TranslatedString,
  assertUnreachable,
} from "@gnu-taler/taler-util";
import { ComponentChildren, VNode, h } from "preact";
import { useRef } from "preact/hooks";
import { composeRef, saveRef } from "./utils.js";
import { useTranslationContext } from "../context/translation.js";

interface Props {
  type?: "info" | "success" | "warning" | "danger" | "error" | "low";
  onClose?: () => void;
  title: TranslatedString | VNode;
  children?: ComponentChildren;
  timeout?: Duration;
  copy?: boolean;
  copyText?: string;
}
export function Attention({
  type = "info",
  title,
  children,
  onClose,
  copy,
  copyText,
  timeout = Duration.getForever(),
}: Props): VNode {
  const { i18n } = useTranslationContext();
  const divHtml = useRef<HTMLDivElement>();
  if (type == "error") type = "danger";
  return (
    <div
      class={`z-100 group attention-${type} mt-2 shadow-lg`}
      role={type === "danger" ? "alert" : "status"}
    >
      {/* {timeout.d_ms === "forever" ? undefined : <style>{`
    .progress {
        animation: notificationTimeoutBar ${Math.round(timeout.d_ms / 1000)}s ease-in-out;
        animation-fill-mode:both; 
    }

    @keyframes notificationTimeoutBar {
      0% { width: 0; }
      100% { width: 100%; }
    }
  `}</style>
    } */}

      <div
        data-timed={timeout.d_ms !== "forever"}
        class="rounded-md data-[timed=true]:rounded-b-none group-[.attention-info]:bg-blue-50 group-[.attention-low]:bg-gray-100 group-[.attention-warning]:bg-yellow-50 group-[.attention-danger]:bg-red-50 group-[.attention-success]:bg-green-50 p-4 shadow"
      >
        <div class="flex">
          <div>
            {type === "low" ? undefined : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                stroke="none"
                viewBox="0 0 24 24"
                fill="currentColor"
                style={{ width: 24, height: 24 }}
                // class="w-8 h-8 group-[.attention-info]:text-blue-400 group-[.attention-warning]:text-yellow-400 group-[.attention-danger]:text-red-400 group-[.attention-success]:text-green-400"
              >
                {(() => {
                  switch (type) {
                    case "info":
                      return (
                        <path
                          fill-rule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z"
                        />
                      );
                    case "warning":
                      return (
                        <path
                          fill-rule="evenodd"
                          d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z"
                        />
                      );
                    case "danger":
                      return (
                        <path
                          fill-rule="evenodd"
                          d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z"
                        />
                      );
                    case "success":
                      return (
                        <path
                          fill-rule="evenodd"
                          d="M7.493 18.75c-.425 0-.82-.236-.975-.632A7.48 7.48 0 016 15.375c0-1.75.599-3.358 1.602-4.634.151-.192.373-.309.6-.397.473-.183.89-.514 1.212-.924a9.042 9.042 0 012.861-2.4c.723-.384 1.35-.956 1.653-1.715a4.498 4.498 0 00.322-1.672V3a.75.75 0 01.75-.75 2.25 2.25 0 012.25 2.25c0 1.152-.26 2.243-.723 3.218-.266.558.107 1.282.725 1.282h3.126c1.026 0 1.945.694 2.054 1.715.045.422.068.85.068 1.285a11.95 11.95 0 01-2.649 7.521c-.388.482-.987.729-1.605.729H14.23c-.483 0-.964-.078-1.423-.23l-3.114-1.04a4.501 4.501 0 00-1.423-.23h-.777zM2.331 10.977a11.969 11.969 0 00-.831 4.398 12 12 0 00.52 3.507c.26.85 1.084 1.368 1.973 1.368H4.9c.445 0 .72-.498.523-.898a8.963 8.963 0 01-.924-3.977c0-1.708.476-3.305 1.302-4.666.245-.403-.028-.959-.5-.959H4.25c-.832 0-1.612.453-1.918 1.227z"
                        />
                      );
                    default:
                      assertUnreachable(type);
                  }
                })()}
              </svg>
            )}
          </div>
          <div ref={composeRef(saveRef(divHtml))} class="ml-3 w-full">
            <h3 class="text-sm font-bold group-[.attention-info]:text-blue-800 group-[.attention-success]:text-green-800 group-[.attention-warning]:text-yellow-800 group-[.attention-danger]:text-red-800">
              {title}
            </h3>
            <div class="mt-2 text-sm group-[.attention-info]:text-blue-700 group-[.attention-warning]:text-yellow-700 group-[.attention-danger]:text-red-700 group-[.attention-success]:text-green-700">
              {children}
            </div>
          </div>
          <div>
            <div class="flex">
              {!copy ? undefined : (
                <button
                  type="button"
                  class="font-semibold items-center rounded bg-transparent px-2 py-1 text-xs text-gray-900  hover:bg-gray-50"
                  onClick={(e) => {
                    e.preventDefault();
                    void navigator.clipboard
                      ?.writeText(copyText ?? fromNodeToText(divHtml.current))
                      .catch((error) =>
                        console.error("Could not copy notification", error),
                      );
                  }}
                  aria-label={i18n.str`Copy details`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="1.5"
                    stroke="currentColor"
                    class="size-6"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 0 1-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 0 1 1.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 0 0-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 0 1-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 0 0-3.375-3.375h-1.5a1.125 1.125 0 0 1-1.125-1.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H9.75"
                    />
                  </svg>
                </button>
              )}
              {onClose && (
                <button
                  type="button"
                  class="font-semibold items-center rounded bg-transparent px-2 py-1 text-xs text-gray-900  hover:bg-gray-50"
                  onClick={(e) => {
                    e.preventDefault();
                    onClose();
                  }}
                  aria-label={i18n.str`Dismiss notification`}
                >
                  <svg
                    class="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      {timeout.d_ms === "forever" ? undefined : (
        <div class="meter group-[.attention-info]:bg-blue-50 group-[.attention-low]:bg-gray-100 group-[.attention-warning]:bg-yellow-50 group-[.attention-danger]:bg-red-50 group-[.attention-success]:bg-green-50 h-1 relative overflow-hidden -mt-1">
          <span class="w-full h-full block">
            <span class="h-full block progress group-[.attention-info]:bg-blue-600 group-[.attention-low]:bg-gray-600 group-[.attention-warning]:bg-yellow-600 group-[.attention-danger]:bg-red-600 group-[.attention-success]:bg-green-600"></span>
          </span>
        </div>
      )}
    </div>
  );
}

function fromNodeToText(node: ChildNode | undefined) {
  var i, result, text, child;
  result = "";

  if (node)
    for (i = 0; i < node.childNodes.length; i++) {
      child = node.childNodes[i];
      text = null;
      if (child.nodeType === 1) {
        text = fromNodeToText(child);
      } else if (child.nodeType === 3) {
        text = child.nodeValue;
      }
      if (text) {
        result += "\n";
        result += text;
      }
    }
  return result;
}
